/// <amd-module name="Wit/WorkItemForm/Details/WorkItemFormControl" />
import { htmlEncode } from "Mention/Autocomplete/AutocompleteUtility";
import { AssociatedAutomationControl } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/AssociatedAutomation/Component/AssociatedAutomationControl";
import { TestStepsControl } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/Component/TestStepsControl";
import { IObservableArray, ObservableArray, ObservableValue, useObservable } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { parseShortTimeString } from "VSS/Platform/String";
import { parseFloatWithLocale } from "VSS/Platform/Util/Number";
import { DatePicker } from "VSSUI/Components/DatePicker/DatePicker";
import { DatePickerType, IDatePickerManualEditingOptions, IDatePickerProps } from "VSSUI/Components/DatePicker/DatePicker.Props";
import { Toggle } from "VSSUI/Components/Toggle/Toggle";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { DropdownExpandableTextField } from "VSSUI/Dropdown";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { Icon } from "VSSUI/Icon";
import { IdentityPickerDropdown } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { IIdentity } from "VSSUI/Persona";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Control } from "Wit/Clients/Wit/Contracts/WorkItemFormLayout";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { getDateInClientTimeZone, getDateInUserTimeZone, parseDateString } from "Wit/Common/DateUtils";
import { EXTENSION_HOST_RESIZED } from "Wit/Common/Extensions";
import { DalFields, FieldType, WellKnownControlNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getCurrentCultureFromLegacyContext, getNumberInUserFormat } from "Wit/Common/Utilities/FormatUtils";
import { getAllowedIdentityTypes } from "Wit/Common/Utilities/Identity";
import { AreaPathDropdown } from "Wit/Common/WorkItemControls/AreaPathDropdown";
import { IterationDropdown } from "Wit/Common/WorkItemControls/IterationDropdown";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { Deployment } from "Wit/Deployment/Components/Deployment";
import { HtmlEditor } from "Wit/HtmlEditor/Components/HtmlEditor";
import { MentionPlugin } from "Wit/HtmlEditor/Plugins/MentionPlugin";
import { convertIIdentityToWorkItemIdentityRef, convertPotentialIdentityRefFromFieldValue } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { ILinkInfo, IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { getAttachmentUri } from "Wit/WorkItemForm/Attachments/useAttachmentUri";
import { ImageUploader } from "Wit/WorkItemForm/Common/ImageUploader";
import { IWorkItemNotificationListener } from "Wit/WorkItemForm/Contracts/Contracts";
import { WorkItemDetailsLinksControl } from "Wit/WorkItemForm/Links";
import * as Resources from "Wit/WorkItemForm/Resources";
import { IField, IFieldSetValueOptions, IWorkItem, WorkItemChangeType } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { ServerDefaultValue } from "Wit/WorkItemModel/RuleEngine/ServerDefaultValue";
import { IWorkItemFieldEventData, IWorkItemFieldEventService, IWorkItemFieldEventType } from "Wit/WorkItemModel/Services/IWorkItemFieldEventService";
import {
    convertFieldValueToInternal,
    fieldHasList,
    getAllowedFieldValues,
    htmlDecode,
    isEditable,
    isFieldValid,
    isNumericField,
    tryNormalizeFieldValue
} from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { LegacyWorkItem } from "Wit/WorkItemModel/WorkItem/LegacyWorkItem";
import * as React from "react";
import { HtmlSanitizer } from "roosterjs-react";
import { ControlDefaultOptionsContext, IWorkItemFormPageContext, WorkItemFormPageContext } from "../Common/Hooks";
import { LegacyWorkItemFormControl } from "./LegacyWorkItemFormControl";
import { WorkItemFormExtensionHost } from "./WorkItemFormExtensionHost";
import { IControlXMLData, readControlXMLData } from "./XMLControlUtils";

export interface IWorkItemFormControlProps {
    controlKey: string;
    control: Control;
    onExtensionLoaded: (workItem: IWorkItem, hostInstance: IWorkItemNotificationListener) => void;
    groupLength: number;
}

const c_defaultDebounceWaitInMs = 250;
const c_reformatUserInputDebounceWaitInMs = 750;

export class WorkItemFormControl extends React.Component<IWorkItemFormControlProps> {
    public static contextType = WorkItemFormPageContext;

    private allowedValues = new ObservableArray<string>([]);
    private allowedValuesListboxItems = new ObservableArray<IListBoxItem>([]);
    private identityPickerProvider: IPeoplePickerProvider;
    private dropdown: DropdownSelection = new DropdownSelection();
    private workItem: IWorkItem;
    private fieldDef: IFieldEntry;
    private field: IField;
    private legacyWorkItem: LegacyWorkItem;
    private _pageContext: IVssPageContext;
    private _linkTypeEnds: ObservableValue<IWorkItemLinkTypeEnd[] | undefined> = new ObservableValue<IWorkItemLinkTypeEnd[] | undefined>(undefined);
    private controlledValue = new ObservableValue<string | boolean | Date | IIdentity | undefined>(undefined);
    // Needed for storing time of date control separatly from origin date value
    private rememberedValue?: Date;
    private decimalControlRef = React.createRef<TextField>();
    private htmlControlRef = React.createRef<HtmlEditor>();
    private readonly = new ObservableValue<boolean>(false);
    private _timerManagement: TimerManagement;
    private _debounceOnChange: (dateStr: string) => void;
    private _datePickerManualEditingOptions = new ObservableValue<IDatePickerManualEditingOptions | undefined>(undefined);
    private _debounceReformatUserInputOfNumericTextControl: (value: string) => void;
    private _workItemFieldEventService: IWorkItemFieldEventService;

    constructor(props: IWorkItemFormControlProps, context: IWorkItemFormPageContext) {
        super(props);

        if (!context.pageContext || context.workItem === null) {
            throw new Error("WorkItemFormPageContext can only be used inside a WorkItemFormPageContextProvider");
        }

        this._pageContext = context.pageContext;
        this._workItemFieldEventService = this._pageContext.getService<IWorkItemFieldEventService>("IWorkItemFieldEventService");
        this.workItem = context.workItem;

        const field = this.workItem.getFieldByName(props.control.id);

        if (field) {
            this.field = field;
            this.fieldDef = field.fieldDefinition;

            if (this.fieldDef.isIdentity && this.fieldDef.type === FieldType.String) {
                const excludeGroups = field.filterByScope?.excludeGroups;
                this.identityPickerProvider = new PeoplePickerProvider(context.pageContext, {
                    identityTypes: getAllowedIdentityTypes(context.pageContext, { excludeGroups })
                });
            }

            let fieldValue = this.workItem.getFieldValueById(
                this.fieldDef.id,
                false,
                false,
                this.fieldDef.isIdentity && this.fieldDef.type === FieldType.String
            );

            this.readonly.value = !isControlEditable(props.control, field);

            if (typeof fieldValue === "string") {
                if (this.fieldDef.type !== FieldType.Html) {
                    fieldValue = HtmlSanitizer.sanitizeHtml(htmlEncode(fieldValue), MentionPlugin.SanitizeHtmlOptions);
                    fieldValue = htmlDecode(fieldValue);
                } else {
                    fieldValue = HtmlSanitizer.sanitizeHtml(fieldValue, MentionPlugin.SanitizeHtmlOptions);
                }
            }

            if (fieldValue instanceof Date) {
                fieldValue = getDateInUserTimeZone(fieldValue);
            } else if (this.fieldDef.type === FieldType.Double) {
                fieldValue = getNumberInUserFormat(fieldValue);
            }

            this.updateControlledFieldValue(fieldValue);
        }

        if (
            props.control.controlType === WellKnownControlNames.AssociatedAutomationControl ||
            props.control.controlType === WellKnownControlNames.TestStepsControl
        ) {
            const linksService = this._pageContext.getService<IWorkItemLinksService>("IWorkItemLinksService");
            linksService.getWitLinkTypeEnds().then(linkTypeEnds => {
                this._linkTypeEnds.value = linkTypeEnds;
            });
        }

        this.updateAllowedValues();

        this._timerManagement = new TimerManagement();
        this._debounceOnChange = this._timerManagement.debounce((dateStr: string) => this.onDateChange(dateStr), c_defaultDebounceWaitInMs);
        this._datePickerManualEditingOptions.value = { datePickerType: DatePickerType.Time };
        this._debounceReformatUserInputOfNumericTextControl = this._timerManagement.debounce(
            this.reformatUserInputOfNumericTextControl,
            c_reformatUserInputDebounceWaitInMs
        );
    }

    public componentDidMount() {
        this.updateAllowedValues();
        this.field?.allowedValues?.subscribe(this.updateAllowedValues);
        this._workItemFieldEventService.subscribe(this.processFieldsChangeEvent, IWorkItemFieldEventType.WorkItemFieldsUpdated);
    }

    private processFieldsChangeEvent = ({ workItem, fieldDefinitionIds, setByRules, workItemChangeType }: IWorkItemFieldEventData) => {
        if (this.workItem.id !== workItem.id) {
            return;
        }

        if (!this.field?.fieldDefinition) {
            return;
        }

        if (fieldDefinitionIds && !fieldDefinitionIds.includes(this.field.fieldDefinition.id)) {
            return;
        }

        const workItemValue = workItem.getFieldValueById(
            this.field.fieldDefinition.id,
            undefined,
            false,
            this.fieldDef.isIdentity && this.fieldDef.type === FieldType.String)
        this.updateAllowedValues();
        this.updateControlledFieldValue(workItemValue, { workItemUpdated: true, setByRules, workItemChangeType });
        this.readonly.value = !isControlEditable(this.props.control, this.field);
    }

    public componentWillUnmount(): void {
        this.field?.allowedValues?.unsubscribe(this.updateAllowedValues);
        this._workItemFieldEventService.unsubscribe(this.processFieldsChangeEvent, IWorkItemFieldEventType.WorkItemFieldsUpdated);
    }

    public componentDidUpdate() {
        this.updateAllowedValues();
    }

    public render(): JSX.Element {
        const control = this.props.control;

        if (control.isContribution && !control.contribution.showOnDeletedWorkItem && this.workItem.isDeleted()) {
            return <></>;
        }

        return (
            <div className="work-item-form-control-wrapper">
                <Observer readonly={this.readonly}>{() => this.renderControl()}</Observer>
            </div>
        );
    }

    private renderControl(): JSX.Element {
        const control = this.props.control;
        if (control.isContribution) {
            return this.renderExtensionControl();
        }

        if (control.controlType === WellKnownControlNames.LinksControl) {
            return <WorkItemDetailsLinksControl control={control} />;
        }

        if (control.controlType === WellKnownControlNames.AssociatedAutomationControl) {
            return this.renderAssociatedAutomationControl();
        }

        if (!this.fieldDef) {
            if (control.controlType === WellKnownControlNames.DeploymentsControl) {
                return (
                    <Deployment
                        workItemId={this.workItem.id}
                        projectName={this.workItem.project?.name ?? "Project"}
                        onRegisterWorkItemListener={listener => this.props.onExtensionLoaded(this.workItem, listener)}
                    />
                );
            }

            if (control.controlType === WellKnownControlNames.LabelControl) {
                return this.renderLabelControl();
            }

            if (control.controlType === WellKnownControlNames.WebpageControl) {
                return this.renderWebpageControl();
            }

            return this.renderUnknownControlPlaceholder();
        }

        if (this.field && fieldHasList(this.field)) {
            return this.renderDropdownControl();
        }

        switch (control.controlType) {
            case WellKnownControlNames.HtmlControl:
                return this.renderHtmlControl();

            case WellKnownControlNames.FieldControl:
                if (this.fieldDef.isIdentity && this.fieldDef.type === FieldType.String) {
                    return this.renderIdentityControl();
                }

                if (this.fieldDef.type === FieldType.Boolean) {
                    return this.renderBooleanControl();
                }

                switch (this.fieldDef.type) {
                    case FieldType.Double:
                        return this.renderDecimalTextControl();
                    case FieldType.Integer:
                        return this.renderIntegerTextControl();
                    default:
                        return this.renderTextControl();
                }

            case WellKnownControlNames.DateControl:
                return this.renderDateControl();

            case WellKnownControlNames.TestStepsControl:
                return this.renderTestStepsControl();

            case WellKnownControlNames.ClassificationControl:
                return this.renderClassificationControl();

            default:
                return this.renderUnknownControlPlaceholder();
        }
    }

    private getLegacyWorkItem(): LegacyWorkItem {
        if (!this.legacyWorkItem || this.legacyWorkItem.rev != this.workItem.revision.value || this.legacyWorkItem.id != this.workItem.id) {
            this.legacyWorkItem = new LegacyWorkItem(this._pageContext, this.workItem, this._linkTypeEnds.value!);
        }

        return this.legacyWorkItem;
    }

    private updateControlledFieldValue(fieldValue: any, options?: { workItemUpdated?: boolean, setByRules?: boolean, workItemChangeType?: WorkItemChangeType }): void {
        let value = fieldValue instanceof ServerDefaultValue ? fieldValue.value : fieldValue;

        if (options?.workItemUpdated && fieldValue instanceof Date) {
            value = getDateInUserTimeZone(fieldValue);
        }

        const isDateParsingFixEnabled = !isFeatureFlagEnabled(this._pageContext, FeatureFlags.DateParsingFixDisabled, false);

        if (this.field && fieldHasList(this.field)) {
            this.controlledValue.value = `${(convertFieldValueToInternal(value, this.field.fieldDefinition.type, isDateParsingFixEnabled).value ?? "")}`;
            if (this.isEmpty()) {
                this.dropdown.clear();
            } else {
                const index = this.allowedValues.value.findIndex(v => v === this.controlledValue.value);
                if (index >= 0 && this.dropdown.selectable(index)) {
                    this.dropdown.select(index);
                }
            }

            return;
        }

        const skipUpdateFieldTypeCondition = isNumericField(this.fieldDef.type);

        const isDecimalControlFocused =
            this.decimalControlRef.current && this.decimalControlRef.current.inputElement.current === document.activeElement;

        const skipUpdate = options
            && options.workItemUpdated
            && skipUpdateFieldTypeCondition
            && options.workItemChangeType === WorkItemChangeType.FieldChange
            && !options.setByRules
            && isDecimalControlFocused;

        if (skipUpdate) {
            // We don't want to update the controlled value if the field is a double and the change was not set by rules 
            // (i.e. user changed the value) because this will cause loosing of user input
            return;
        }

        if (isNumericField(this.fieldDef.type)) {
            if (options?.workItemUpdated && this.fieldDef.type === FieldType.Double) {
                value = getNumberInUserFormat(value);
            }

            this.controlledValue.value = (value ?? "") + "";
        } else {
            this.controlledValue.value = value;
        }
    }

    private parseNumber(num: string): number | undefined {
        if (num === undefined || num.trim() === "") {
            return undefined;
        }

        const currentCulture = getCurrentCultureFromLegacyContext();
        return parseFloatWithLocale(num, currentCulture);
    }

    private isNumericInputValid(value: string, valueParser?: (item: string) => string | undefined): boolean {
        const valueToCheck = valueParser ? valueParser(value) : value;
        if (!valueToCheck || valueToCheck.trim() === "") {
            return true;
        }

        // Coerce to number
        const testedValue: number = (valueToCheck as any) - 0;

        if (isNaN(testedValue)) {
            // Reject if it has any invalid characters
            return false;
        }
        return true;
    }

    private renderUnknownControlPlaceholder(): JSX.Element {
        return <div>{JSON.stringify(this.props.control, null, 2)}</div>;
    }

    private renderTextControl() {
        const { control, controlKey } = this.props;
        return (
            <ControlWrapper
                control={control}
                empty={this.isEmpty()}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                <TextField
                    key={controlKey}
                    ariaLabelledBy={control.label}
                    className="work-item-form-control flex-column"
                    containerClassName="work-item-form-control-value-parent"
                    inputId={getInputId(control.label)}
                    inputType={"text"}
                    disabled={this.readonly.value}
                    value={this.controlledValue as ObservableValue<string | undefined>}
                    onChange={(ev, value) => {
                        this.controlledValue.value = value;
                        this.userChangedValue(value);
                    }}
                    width={TextFieldWidth.auto}
                />
            </ControlWrapper>
        );
    }

    private renderDecimalTextControl() {
        const { control, controlKey } = this.props;

        return (
            <ControlWrapper
                control={control}
                empty={this.isEmpty()}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                <TextField
                    key={controlKey}
                    ariaLabelledBy={control.label}
                    className="work-item-form-control flex-column"
                    containerClassName="work-item-form-control-value-parent"
                    inputId={getInputId(control.label)}
                    onBlur={() => this.reformatUserInputOfNumericTextControl()}
                    inputType="text"
                    disabled={this.readonly.value}
                    value={this.controlledValue as ObservableValue<string | undefined>}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
                        const normalizedValue = tryNormalizeFieldValue(value, this.field).toString();
                        this.controlledValue.value = normalizedValue;
                        this.userChangedValue(normalizedValue);
                    }}
                    rows={4}
                    width={TextFieldWidth.auto}
                    ref={this.decimalControlRef}
                />
            </ControlWrapper>
        );
    }

    private renderIntegerTextControl() {
        const { control, controlKey } = this.props;

        return (
            <ControlWrapper
                control={control}
                empty={this.isEmpty()}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                <TextField
                    key={controlKey}
                    ariaLabelledBy={control.label}
                    className="work-item-form-control flex-column"
                    containerClassName="work-item-form-control-value-parent"
                    inputId={getInputId(control.label)}
                    onBlur={() => this.reformatUserInputOfNumericTextControl(undefined, true)}
                    inputType="text"
                    disabled={this.readonly.value}
                    value={this.controlledValue as ObservableValue<string | undefined>}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
                        const normalizedValue = tryNormalizeFieldValue(value, this.field).toString();
                        this.controlledValue.value = normalizedValue;
                        this.userChangedValue(normalizedValue);
                    }}
                    rows={4}
                    width={TextFieldWidth.auto}
                />
            </ControlWrapper>
        );
    }

    //reformat and update user input to a valid number
    //for example 0007000 -> 7000
    private reformatUserInputOfNumericTextControl = (value?: string, floorParsedValue?: boolean): void => {
        const valueToCheck = this.controlledValue.value?.toString();
        let parsedValue = this.parseNumber(valueToCheck ?? value ?? "");
        let stringValue = parsedValue?.toString() ?? "";
        if (parsedValue && this.isNumericInputValid(stringValue)) {
            if (floorParsedValue) {
                parsedValue = Math.floor(parsedValue as number);
                stringValue = parsedValue.toString();
            }

            this.controlledValue.value = getNumberInUserFormat(parsedValue);
            this.userChangedValue(stringValue);
        }
    };

    private renderDropdownControl(): JSX.Element {
        const { controlKey, control } = this.props;
        return (
            <Observer allowedValues={{ observableValue: this.allowedValues, filter: this.onAllowedValuesChange }}>
                {() => {
                    return (
                        <ControlWrapper
                            control={control}
                            empty={this.isEmpty()}
                            field={this.field}
                            readonly={this.readonly.value}
                            workItem={this.workItem}
                        >
                            <EditableDropdown
                                key={controlKey}
                                className="flex-grow work-item-form-control"
                                allowTextSelection
                                ariaLabelledBy={control.label}
                                disabled={this.readonly.value}
                                inputId={getInputId(control.label)}
                                items={this.allowedValuesListboxItems}
                                onValueChange={(selection: IListBoxItem<string>) => {
                                    this.controlledValue.value = selection?.text;
                                    this.userChangedValue(selection?.text);
                                }}
                                renderExpandable={props => <DropdownExpandableTextField {...props} />}
                                selectedText={this.controlledValue as ObservableValue<string>}
                                selection={this.dropdown}
                                allowFreeform={!this.field.lists?.allowedValues || this.field.lists?.allowExistingValue}
                            />
                        </ControlWrapper>
                    );
                }}
            </Observer>
        );
    }

    private updateAllowedValues = (): void => {
        if (!(this.allowedValues.value.length || (this.field && fieldHasList(this.field)))) {
            // We only need to update allowed values if we have values
            return;
        }

        const allowedValues = getAllowedFieldValues(this.field, this.workItem);
        if (this.areAllowedValuesUnchanged(allowedValues)) {
            return;
        }

        // Update our copy of the observable
        this.allowedValues.value = allowedValues.value;
        this.onAllowedValuesChange();
    };

    private areAllowedValuesUnchanged(allowedValues: IObservableArray<string>) {
        if (this.allowedValues === allowedValues) {
            // If it is the same observable, do nothing.
            return true;
        }

        if (this.allowedValues.length !== allowedValues.length) {
            return false;
        }

        let i = -1;
        for (const value of this.allowedValues.value) {
            if (value !== allowedValues.value[++i]) {
                return false;
            }
        }

        return true;
    }

    private onAllowedValuesChange = () => {
        this.allowedValuesListboxItems.value = this.allowedValues.value.map(value => {
            return { text: value, id: value };
        });
    };

    private renderBooleanControl(): JSX.Element {
        const control = this.props.control;
        return (
            <ControlWrapper
                control={control}
                empty={false}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                <Observer controlledValue={this.controlledValue as ObservableValue<boolean>}>
                    {({ controlledValue }) => (
                        <Toggle
                            checked={controlledValue}
                            className="work-item-form-control"
                            disabled={this.readonly.value}
                            text={controlledValue ? Resources.TrueString : Resources.FalseString}
                            onChange={(event, checked) => {
                                this.controlledValue.value = checked;
                                this.userChangedValue(checked);
                            }}
                        />
                    )}
                </Observer>
            </ControlWrapper>
        );
    }

    private renderDateControl(): JSX.Element {
        const { control, controlKey } = this.props;
        const datePickerCommonProps: IDatePickerProps = {
            disabled: this.readonly.value,
            className: "work-item-form-control",
            containerClassName: css("work-item-form-control-value-parent", "date"),
            showClear: true,
            value: this.controlledValue as ObservableValue<Date | string>,
            onChange: this.onDateChange,
            hasError: !this.isEmpty() && !isFieldValid(this.field)
        };

        return (
            <ControlWrapper
                control={control}
                empty={this.isEmpty()}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                <DatePicker ariaLabel={format(Resources.DateField, control.label)} key={controlKey} inputId={getInputId(control.label)} {...datePickerCommonProps} />
                <DatePicker
                    ariaLabel={format(Resources.TimeField, control.label)}
                    key={controlKey + "-time"}
                    inputId={getInputId(control.label) + "-time"}
                    {...datePickerCommonProps}
                    containerClassName="work-item-form-control-value-parent time"
                    debounceOnChange={this._debounceOnChange}
                    manualEditingOptions={this._datePickerManualEditingOptions}
                />
            </ControlWrapper>
        );
    }

    private renderIdentityControl(): JSX.Element {
        const { control, controlKey } = this.props;
        return (
            <ControlWrapper
                control={control}
                empty={this.isEmpty()}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                <IdentityPickerDropdown
                    ariaLabelledBy={control.label}
                    className="work-item-form-control"
                    key={controlKey}
                    disabled={this.readonly.value}
                    inputId={getInputId(control.label)}
                    onChange={identity => {
                        this.controlledValue.value = identity;
                        const workItemIdentityRef = identity ? convertIIdentityToWorkItemIdentityRef(this._pageContext, identity) : undefined;
                        this.userChangedValue(convertPotentialIdentityRefFromFieldValue(this._pageContext, workItemIdentityRef), {
                            eagerIdentityValidation: true
                        });
                    }}
                    pickerProvider={this.identityPickerProvider}
                    value={this.controlledValue as ObservableValue<IIdentity | undefined>}
                />
            </ControlWrapper>
        );
    }

    private renderHtmlControl(): JSX.Element {
        const imageUploader: ImageUploader = new ImageUploader(this._pageContext, this.workItem);
        const control = this.props.control;

        return (
            <ControlWrapper
                noLabel
                control={control}
                empty={this.isEmpty()}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                <HtmlEditor
                    ariaLabel={control.label}
                    className="work-item-form-control"
                    htmlContent={(this.controlledValue.value as string) || ""}
                    onChange={newValue => {
                        if (this.controlledValue.value !== newValue) {
                            this.controlledValue.value = newValue;
                            this.userChangedValue(newValue);
                        }
                    }}
                    placeholder={format(Resources.HtmlFieldPlaceholder, control.label)}
                    readonly={this.readonly.value}
                    ref={this.htmlControlRef}
                    telemetryContext={{ controlName: "HtmlEditor" }}
                    uploadImageHandler={file => imageUploader.upload(file, { useAreaPath: true })}
                />
            </ControlWrapper>
        );
    }

    private renderExtensionControl(): JSX.Element {
        const { control } = this.props;
        return (
            <ControlWrapper
                control={control}
                height={control.contribution.height}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
                resizable={true}
            >
                <WorkItemFormExtensionHost
                    contributionId={control.contribution.contributionId}
                    context={{ witInputs: control.contribution.inputs }}
                    onLoaded={this.props.onExtensionLoaded}
                />
            </ControlWrapper>
        );
    }

    private renderTestStepsControl(): JSX.Element {
        if (isFeatureEnabled(this._pageContext, "ms.vss-test-web.nwp-test-steps-control-feature", false)) {
            return <TestStepsControl workItem={this.workItem} />;
        } else {
            return (
                <LegacyWorkItemFormControl
                    module="TestManagement/Scripts/Shared/TestStepsProxy"
                    wrappedType="test-steps-proxy"
                    legacyWorkItem={() => {
                        const legacyWorkItem = this.getLegacyWorkItem();
                        legacyWorkItem.getAttachmentUri = (link: ILinkInfo) => getAttachmentUri(this._pageContext, this.workItem.project.guid, link);
                        return legacyWorkItem;
                    }}
                    linkTypeEnds={this._linkTypeEnds}
                />
            );
        }
    }

    private renderAssociatedAutomationControl(): JSX.Element {
        if (isFeatureFlagEnabled(this._pageContext, "WebAccess.TestManagement.EnableNwpAssociatedAutomationControl", false)) {
            return <AssociatedAutomationControl workItem={this.workItem} />;
        } else {
            return (
                <LegacyWorkItemFormControl
                    module="TestManagement/Scripts/Shared/AssociatedAutomationProxy"
                    wrappedType="associated-automation-proxy"
                    legacyWorkItem={() => {
                        return this.getLegacyWorkItem();
                    }}
                    linkTypeEnds={this._linkTypeEnds}
                />
            );
        }
    }

    private renderLabelControl(): JSX.Element {
        const controlXMLData = readControlXMLData(this.props.control.metadata);
        const labelElements: JSX.Element[] = [];

        if (controlXMLData) {
            controlXMLData.labelData.forEach((labelPiece, index) => {
                if (labelPiece.link && labelPiece.content) {
                    labelElements.push(
                        <a key={`label-control-${index}`} href={labelPiece.link.url} target="_blank">
                            {labelPiece.content}
                        </a>
                    );
                } else if (labelPiece.content) {
                    labelElements.push(<span key={`label-control-${index}`}>{labelPiece.content}</span>);
                }
            });
        }

        return <div className="font-size secondary-text">{labelElements}</div>;
    }

    private renderWebpageControl(): JSX.Element {
        const controlXMLData = readControlXMLData(this.props.control.metadata);

        if (!controlXMLData) {
            return <div />;
        }

        if (this.props.control.height) {
            controlXMLData.height = this.props.control.height;
        } else {
            if (this.props.groupLength > 1) {
                controlXMLData.height = 200;
            } else {
                controlXMLData.height = 300;
            }
        }

        return (
            <LegacyComponent
                modules={["WorkItemTracking/SharedScripts/WebPageControlProxy"]}
                wrappedType="webpage-control-proxy"
                workItem={this.getLegacyWorkItem()}
                options={controlXMLData}
            />
        );
    }

    private renderClassificationControl(): JSX.Element {
        const { controlKey, control } = this.props;
        const commonProps = {
            ariaLabelledBy: control.label,
            className: css("flex-grow work-item-control-control", this.readonly.value && "readOnly"),
            disabled: this.readonly.value,
            inputId: getInputId(control.label),
            placeHolder: control.watermark,
            key: controlKey,
            selectedText: this.controlledValue as ObservableValue<string>,
            onValueChange: (value: IListBoxItem<WorkItemClassificationNode> | undefined) => {
                this.controlledValue.value = value?.text;
                this.userChangedValue(value?.text);
            }
        };
        return (
            <ControlWrapper
                control={control}
                empty={this.isEmpty()}
                field={this.field}
                readonly={this.readonly.value}
                workItem={this.workItem}
            >
                {this.field.fieldDefinition.id == DalFields.AreaPathId && <AreaPathDropdown {...commonProps} />}
                {this.field.fieldDefinition.id == DalFields.IterationPathId && <IterationDropdown {...commonProps} />}
            </ControlWrapper>
        );
    }

    private userChangedValue(
        newValue: any,
        options?: { valueParser?: (item: string) => number | string | undefined; eagerIdentityValidation?: boolean }
    ) {
        let parsedNumber: number | undefined = undefined;
        if (isNumericField(this.fieldDef.type)) {
            if (newValue?.trim() === "") {
                newValue = undefined;
            } else {
                if (this.fieldDef.type === FieldType.Integer) {
                    parsedNumber = options?.valueParser ? (options.valueParser(newValue) as any) - 0 : newValue - 0;

                    if (!isNaN(parsedNumber)) {
                        parsedNumber = Math.floor(parsedNumber);
                    } else {
                        parsedNumber = undefined;
                    }
                }
            }
        }
        const setValueoptions: IFieldSetValueOptions = {};
        if (options?.eagerIdentityValidation) {
            setValueoptions.fireIdentityEagerValidation = true;
        }
        this.workItem.setFieldValueById(this.fieldDef.id, parsedNumber ?? newValue, setValueoptions);
    }

    private isEmpty() {
        return this.controlledValue.value === undefined || this.controlledValue.value === "";
    }

    private onDateChange = (value: string | Date | undefined) => {
        let valueToValidate = value;
        const date = this.controlledValue.value instanceof Date ? new Date(this.controlledValue.value.getTime()) : this.rememberedValue;
        if (typeof value === "string") {
            if (value) {
                const dateStringToParse = value.trim().replace(/\./g, ":");
                let parsedDate = parseShortTimeString(dateStringToParse);
                if (!parsedDate) {
                    parsedDate = parseDateString(dateStringToParse, true);
                }

                value = parsedDate;
                if (value) {
                    date?.setHours(value.getHours());
                    date?.setMinutes(value.getMinutes());
                    valueToValidate = getDateInClientTimeZone(date || new Date());
                } else {
                    // Triger validation message in case date is not parsed
                    valueToValidate = "invalid";
                }
                // Set date from time input, to combine time with date selected on regular date picker
                this.rememberedValue = date ?? value;
                value = date;
            } else {
                // Reset time when value in time input is deleted
                date?.setHours(0);
                date?.setMinutes(0);
                valueToValidate = value = date;
            }
        } else if (date && value) {
            // When date in date picker is changed, set time to date
            value.setHours(date.getHours());
            value.setMinutes(date.getMinutes());
            valueToValidate = getDateInClientTimeZone(value);
        } else if (value) {
            valueToValidate = getDateInClientTimeZone(value);
        }
        this.userChangedValue(valueToValidate);
        this.controlledValue.value = value;
    };
}

interface IControlWrapperProps {
    control: Control;
    empty?: boolean;
    field?: IField;
    height?: number;
    noLabel?: boolean;
    readonly?: boolean;
    workItem: IWorkItem;
    resizable?: boolean;
}

function ControlWrapper({
    control,
    empty,
    field,
    height,
    children,
    noLabel,
    readonly,
    workItem,
    resizable
}: React.PropsWithChildren<IControlWrapperProps>) {
    let tooltipText = control.label;
    const workItemFormControlContentRef = React.createRef<HTMLDivElement>();
    const controlXMLData = React.useRef<IControlXMLData | undefined>(readControlXMLData(control.metadata)).current;
    const [heightObservable, setHeight] = useObservable(height);

    React.useEffect(() => {
        if (resizable) {
            workItemFormControlContentRef.current?.addEventListener(EXTENSION_HOST_RESIZED, e => {
                setHeight((e as CustomEvent).detail.height);
                e.stopPropagation();
            });
        }
    }, []);

    if (field) {
        tooltipText = format(Resources.FieldNameTooltip, field.fieldDefinition.name);
        const helpText = workItem.getTypeData().rules.fieldHelpTexts[field.fieldDefinition.id];
        if (helpText) {
            tooltipText = helpText + "\n" + tooltipText;
        }
    }

    const xmlDataIsPresent = controlXMLData?.labelData && controlXMLData.labelData.length > 0;

    return (
        <ControlDefaultOptionsContext.Consumer>
            {defaultOptions => (
                <div className={css("flex-column", !defaultOptions.showEmptyReadOnlyFields && readonly && empty && "hidden")}>
                    {!noLabel && (control.label || xmlDataIsPresent) && (
                        <div className="flex-row">
                            <FormControlLabel
                                labelText={control.label}
                                tooltipText={tooltipText}
                                metadata={controlXMLData}
                            />
                            <Observer infoText={workItem.infoText}>
                                {() => (
                                    <ConditionalChildren renderChildren={!field ? false : !isFieldValid(field)}>
                                        <Icon
                                            ariaLabel={format(Resources.InvalidControl, control.label)}
                                            className="padding-left-4 error-text"
                                            key="invalid-control-icon"
                                            iconName="AlertSolid"
                                        />
                                    </ConditionalChildren>
                                )}
                            </Observer>
                        </div>
                    )}
                    <Observer height={heightObservable}>
                        {(props: { height: number | undefined }) => (
                            <div
                                ref={workItemFormControlContentRef}
                                className={css("work-item-form-control-content", defaultOptions.showChromeBorder && "show-border")}
                                style={props.height ? { height: props.height } : undefined}
                            >
                                <ConditionalChildren renderChildren={readonly}>
                                    <Icon
                                        className="flex-column work-item-form-readonly-lock-icon"
                                        key="readonly-icon"
                                        iconName="Lock"
                                        tooltipProps={{ text: format(Resources.ReadonlyFieldLockTooltip, field?.fieldDefinition.name) }}
                                    />
                                </ConditionalChildren>

                                {children}
                            </div>
                        )}
                    </Observer>
                </div>
            )}
        </ControlDefaultOptionsContext.Consumer>
    );
}
/**
 * Component to display label for the control on a WI form.
 * 
 * We use field label text to display the link only in case there is only link specified
 * without additional text. See example below (It will render clickable `Effort`):
 * Otherwise we use the label text from the control metadata (OWP behavior).
 * 
 * @example
 * <Control Label="Effort" Type="FieldControl" FieldName="Microsoft.VSTS.Scheduling.Effort">
 *     <LabelText>
 *     <Text>
 *         <Link UrlRoot="https://www.microsoft.com" UrlPath="/en-us/?ql=2"></Link>
 *     </Text>
 *     </LabelText>
 * </Control>
 * 
 * 
 * @param labelText Standard label text
 * @param tooltipText Tooltip text
 * @metadata Metadata for the control from XML WIT definition.
 */
function FormControlLabel({
    labelText,
    tooltipText,
    metadata
}: React.PropsWithChildren<{ labelText: string; tooltipText: string; metadata?: IControlXMLData }>) {
    let labelContents: any = labelText;
    if (metadata?.labelData?.length) {
        labelContents = metadata.labelData.map((data, index) =>
            data.link?.url ? (
                <Link key={`form-control-label-${index}`} href={data.link.url} target="_blank">
                    {data.content ? data.content : metadata.labelData?.length > 1 ? undefined : labelText}
                </Link>
            ) : data.content ? (
                <span key={`label-control-label-${index}`}>{data.content}</span>
            ) : undefined
        );
    }

    return (
        <Tooltip className="work-item-form-control-tooltip" text={tooltipText}>
            <label className={"workitemcontrol-label"} htmlFor={getSafeId(getInputId(labelText))} id={getSafeId(labelText)}>
                {labelContents}
            </label>
        </Tooltip>
    );
}

function isControlEditable(control: Control, field: IField) {
    return !control.readonly && isEditable(field);
}

function getInputId(controlName: string): string {
    return `${controlName}-input`;
}
